/**
 * Deep links for external users (ARAP-1911).
 *
 * External users sign in only through Control Center, which always sends them to the AWI home page, so AWI can't
 * carry the page they asked for through the sign-in the way it does for internal users (ARAP-1867). Instead, when a
 * signed-out external user opens an AWI link, the page is remembered here, and opened after they come back signed in.
 *
 * localStorage is used, not sessionStorage, because Control Center may open AWI in a new tab. Only the path is kept
 * (no tokens), for PENDING_DEEP_LINK_TTL_MS, and only for AWI's own external pages.
 */
import { isSafeReturnPath } from '../../utils';

export const PENDING_DEEP_LINK_KEY = 'awi-pending-deep-link';
export const PENDING_DEEP_LINK_TTL_MS = 30 * 60 * 1000;
const MAX_PATH_LENGTH = 2048;

/** External pages a link may lead to. Internal, admin and error pages are never remembered. */
export const EXTERNAL_DEEP_LINK_PAGES = [
  '/subscriptions',
  '/embedded-dashboard',
  '/reports-and-dashboards',
  '/edit-subscription',
  '/help',
  '/settings',
  '/home/favorited-reports-dashboards',
  '/home/favorited-insights',
];

interface PendingDeepLink {
  path: string;
  savedAt: number;
}

/** True for a safe, in-app path to one of AWI's external pages, for example /subscriptions?ref=abc. */
export function isExternalDeepLink(path: string | null | undefined): boolean {
  if (!path || path.length > MAX_PATH_LENGTH || !isSafeReturnPath(path) || /[\\\s]/.test(path)) {
    return false;
  }
  const lower = path.toLowerCase();
  return EXTERNAL_DEEP_LINK_PAGES.some(page => lower === page || lower.startsWith(page + '/') || lower.startsWith(page + '?'));
}

/** Remembers the page a signed-out external user asked for. Returns false (and keeps nothing) for any other path. */
export function savePendingDeepLink(path: string, now: number = Date.now(), storage: Storage = localStorage): boolean {
  if (!isExternalDeepLink(path)) {
    return false;
  }
  try {
    storage.setItem(PENDING_DEEP_LINK_KEY, JSON.stringify({ path, savedAt: now } satisfies PendingDeepLink));
    return true;
  } catch {
    return false;
  }
}

function read(now: number, storage: Storage): string | null {
  try {
    const raw = storage.getItem(PENDING_DEEP_LINK_KEY);
    if (!raw) {
      return null;
    }
    const saved = JSON.parse(raw) as Partial<PendingDeepLink>;
    const fresh = typeof saved.savedAt === 'number' && now - saved.savedAt >= 0 && now - saved.savedAt <= PENDING_DEEP_LINK_TTL_MS;
    return fresh && typeof saved.path === 'string' && isExternalDeepLink(saved.path) ? saved.path : null;
  } catch {
    return null;
  }
}

/** True while a remembered page is waiting, so the sign-in message can say they'll come back to it. */
export function hasPendingDeepLink(now: number = Date.now(), storage: Storage = localStorage): boolean {
  return read(now, storage) !== null;
}

/** The remembered page, if it's still fresh, and forgets it either way so it opens only once. */
export function takePendingDeepLink(now: number = Date.now(), storage: Storage = localStorage): string | null {
  const path = read(now, storage);
  try {
    storage.removeItem(PENDING_DEEP_LINK_KEY);
  } catch {
    // nothing to clean up
  }
  return path;
}
